<template>
  <Modal
    v-model="shown"
    :title="
      updateMode ? 'Update your Team Effectiveness Review' : 'Start a new Team Effectiveness Review'
    "
  >
    <div style="width: 100%" class="form-group">
      <div class="form-group" :class="{ 'has-error': v$.name.$error }">
        <label for="name">What would you like to call the review?</label>
        <input
          id="name"
          class="form-control input-lg"
          style="width: 100%; max-width: 100%"
          v-model="name"
          type="text"
          maxlength="80"
          :placeholder="'(e.g. ' + exampleReviewName + ')'"
          @change="onChange"
          @blur="v$.name.$touch()"
          data-test="review-name-input"
        />
        <small v-if="!v$.name.error" class="error-feedback text-danger">
          Please provide a name for your review</small
        >
      </div>
      <div class="form-group" :class="{ 'has-error': v$.deadline.$error }">
        <label for="deadline">When would you like the review to be completed by?</label>
        <input
          id="deadline"
          class="form-control input-lg"
          v-model="deadline"
          type="date"
          @change="onChange"
          @blur="v$.deadline.$touch()"
          data-test="review-deadline-input"
        />
        <small v-if="!v$.deadline.error" class="error-feedback text-danger">
          Please enter a future expected competion date.</small
        >
        <p class="help-block">
          This helps set expectations for team members.
          <Popover title="Setting a date" placement="right" custom-class="popover-within-modal">
            <template>
              <a class="a-minorAction showPopover" href="javascript:void(0)" aria-label="popover"
                ><i class="glyphicon glyphicon-info-sign"
              /></a>
            </template>
            <template #popover>
              <div v-text-from-prismic="'ter-deadline-guidance-tooltip'">
                Some default text if there isn't a string available...
              </div>
            </template>
          </Popover>
        </p>
      </div>
      <div class="row" v-if="errors && errors.length">
        <br />

        <div class="col-sm-12">
          <p class="col-sm-12 text-danger text-left" v-for="error in errors" v-bind:key="error">
            {{ error }}
          </p>
        </div>
      </div>
    </div>
    <template #footer>
      <div>
        <button
          type="button"
          class="btn btn-lg btn-danger pull-left"
          v-if="updateMode && !isConfirmingDelete"
          @click="deleteClicked"
        >
          Delete
        </button>
        <button
          type="button"
          class="btn btn-lg btn-danger"
          v-if="updateMode && isConfirmingDelete"
          @click="confirmDeleteClicked"
        >
          Sure - responses will be lost?
        </button>
        <button
          type="button"
          class="btn btn-lg btn-primary"
          v-if="!updateMode"
          @click="createClicked"
          :disabled="v$.$invalid || isSaving"
        >
          Start review
        </button>
        <button
          type="button"
          class="btn btn-lg btn-primary"
          v-if="updateMode"
          @click="updateClicked"
          :disabled="v$.$invalid || !hasPendingChanges || isSaving"
        >
          Save changes
        </button>
        <button type="button" class="btn btn-lg btn-default" @click="cancel">Cancel</button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Modal, Popover } from "uiv";
import axios from "axios";
import moment from "dayjs";
import { useVuelidate } from "@vuelidate/core";
import { maxLength, required } from "@vuelidate/validators";
import { TeamEffectivenessReviewApiModel } from "../../api-models";

let resolveCallback: ((boolean) => void) | null = null;

let rejectCallback: ((reason?: any) => void) | null = null;

const teamId = ref("");

const shown = ref(false);

const errors = ref<string[]>([]);

const isSaving = ref(false);

const review = ref<TeamEffectivenessReviewApiModel | null>(null);

const name = ref("");

const deadline = ref<string | null>(null);

const updateMode = ref(false);

const isConfirmingDelete = ref(false);

const validDate = (value: string) => {
  const date = moment(value, "YYYY-MM-DD");
  return (
    date.isValid() &&
    date.isAfter(moment().subtract(1, "day").toDate()) &&
    /20\d\d-\d\d-\d\d/.test(value)
  );
};

const rules = {
  name: {
    required,
    maxLength: maxLength(80),
  },
  deadline: {
    required,
    validDate,
  },
};

const v$ = useVuelidate(rules, { name, deadline }, { $autoDirty: true });

const exampleReviewName = computed(() => {
  return `Team Effectiveness - ${moment().format("MMM YYYY")}`;
});

const hasPendingChanges = computed(() => {
  if (!review.value) {
    return false;
  }

  return (
    name.value !== review.value.name ||
    moment(review.value.deadline).format("DD/MM/YYYY") !==
      moment(deadline.value || "").format("DD/MM/YYYY")
  );
});

function close(changesMade: boolean): void {
  shown.value = false;
  if (resolveCallback) {
    resolveCallback(changesMade);
  }
}

function cancel(): void {
  close(false);
}

function open(
  id: string,
  existingReview: TeamEffectivenessReviewApiModel | null,
): Promise<boolean> {
  isConfirmingDelete.value = false;
  isSaving.value = false;
  errors.value = [];
  if (v$.value) {
    v$.value.$reset();
  }

  teamId.value = id;
  review.value = existingReview;
  updateMode.value = !!existingReview;
  shown.value = true;

  if (review.value) {
    name.value = review.value.name;
    deadline.value = moment(review.value.deadline).format("YYYY-MM-DD");
  } else {
    name.value = "";
    deadline.value = null;
  }

  return new Promise((resolve, reject) => {
    resolveCallback = resolve;
    rejectCallback = reject;
  });
}

function createClicked(): void {
  if (isSaving.value) {
    return;
  }
  if (v$.value.$touch) {
    v$.value.$touch();
  } else {
    throw new Error("Vuelidate v$.value.$touch() unavailable");
  }
  if (v$.value.$invalid) {
    return;
  }

  errors.value = [];
  axios
    .post("/api/teameffectivenessreview/", {
      name: name.value,
      deadline: deadline.value,
      subjectTeamId: teamId.value,
    })
    .then((resp) => {
      close(true);
    })
    .catch((err) => {
      errors.value = ["There was a problem creating the review, please try again."];
      if (err?.response?.data?.length && err?.response?.data?.length < 300) {
        errors.value.push(err.response.data);
      }
    })
    .finally(() => {
      isSaving.value = false;
    });
}

function updateClicked(): void {
  if (!review.value) {
    return;
  }

  if (isSaving.value) {
    return;
  }
  if (v$.value) {
    v$.value.$touch();
  } else {
    throw new Error("Vuelidate this.$v.$touch() unavailable");
  }
  if (v$.value.$invalid) {
    return;
  }

  axios
    .patch("/api/teameffectivenessreview/", {
      name: name.value,
      deadline: deadline.value,
      Id: review.value.id,
    })
    .then(() => {
      close(true);
    })
    .catch((err) => {
      errors.value = ["There was a problem updating the review, please try again."];
      if (err?.response?.data?.length && err?.response?.data?.length < 300) {
        errors.value.push(err.response.data);
      }
    });
}

function deleteClicked(): void {
  if (!review.value) {
    return;
  }

  isConfirmingDelete.value = true;
}

function confirmDeleteClicked(): void {
  if (!review.value) {
    return;
  }

  axios
    .delete(`/api/teameffectivenessreview/${review.value.id}`)
    .then(() => {
      close(true);
    })
    .catch((err) => {
      errors.value = ["There was a problem deleting the review, please try again."];
      if (err?.response?.data?.length && err?.response?.data?.length < 300) {
        errors.value.push(err.response.data);
      }
    });
}

function onChange(): void {
  isConfirmingDelete.value = false;
}

defineExpose({
  open,
});
</script>

<style lang="less"></style>
